import { getSupabase } from '@/lib/supabase';
import type { UserId } from '@/schemas/ids';
import type { ImageRecord } from '@/schemas/image';
import { decodeRowToImage, type ImageRow } from '../codec';
import { getUserSyncState, setUserLastDownloadedAt } from '../state';

export interface DownloadResult {
  images: Array<Omit<ImageRecord, 'localBlob' | 'localThumbnail'>>;
}

/**
 * Download remote image metadata from Supabase
 * Note: Actual image blobs are stored in Supabase Storage and accessed via URLs
 */
export async function downloadRemoteImages(userId: UserId): Promise<DownloadResult> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync images');
  }

  const { lastDownloadedAt = 0 } = await getUserSyncState(userId, 'images');

  // Convert timestamp to ISO string for Supabase query
  const lastDownloadedAtISO = new Date(lastDownloadedAt).toISOString();

  const { data, error } = await client
    .from('images')
    .select<'*', ImageRow>('*')
    .eq('user_id', userId)
    .gt('created_at', lastDownloadedAtISO)
    // eslint-disable-next-line unicorn/no-null -- Supabase API uses SQL NULL, not undefined
    .is('removed_at', null) // Only get non-removed images
    .order('created_at', { ascending: true })
    .limit(1000);

  if (error) throw error;

  const images: Array<Omit<ImageRecord, 'localBlob' | 'localThumbnail'>> = [];

  for (const row of data ?? []) {
    try {
      // Get public URLs from storage paths
      const {
        data: { publicUrl },
      } = client.storage.from('images').getPublicUrl(row.storage_path);
      const {
        data: { publicUrl: thumbnailPublicUrl },
      } = client.storage.from('images').getPublicUrl(row.thumbnail_path);

      const image = decodeRowToImage(row, publicUrl, thumbnailPublicUrl);
      images.push(image);
    } catch (error) {
      console.error(`Failed to decode image row ${row.id}:`, error);
    }
  }

  // Update download cursor
  if (images.length > 0) {
    const lastImage = images.at(-1);
    if (lastImage) {
      await setUserLastDownloadedAt(userId, 'images', lastImage.createdAt);
    }
  }

  return { images };
}
